import { MONTHS } from '@constants/calendar';
import type { PersistedEstimate } from '@hooks/use-persisted-estimate';
import { useEffect, useMemo, useState } from 'react';
import type { PricingEstimationService } from '../../../../types';
import type { EstimateServiceBreakdown } from '../../../utils/estimateDisplay';
import { SERVICES } from '../../../utils/estimateDisplay';

const isService = (key: string): key is PricingEstimationService =>
	SERVICES.some((s) => s.key === key);

const toDateString = (
	persistedDate: PersistedEstimate['weddingDate'] | undefined,
): string => {
	if (!persistedDate) return '';
	if (persistedDate.type === 'exact') return persistedDate.date;
	const monthIndex = MONTHS.indexOf(persistedDate.month);
	if (monthIndex === -1) return '';
	const mm = String(monthIndex + 1).padStart(2, '0');
	return `${persistedDate.year}-${mm}-01`;
};

const getInitialServices = (
	serviceBreakdown: EstimateServiceBreakdown | null,
): PricingEstimationService[] =>
	Object.keys(serviceBreakdown ?? {}).filter(isService);

interface UseEditEstimateFormStateParams {
	isModalOpen: boolean;
	persistedEstimate: PersistedEstimate | null;
	serviceBreakdown: EstimateServiceBreakdown | null;
}

export const useEditEstimateFormState = ({
	isModalOpen,
	persistedEstimate,
	serviceBreakdown,
}: UseEditEstimateFormStateParams) => {
	const [selectedServices, setSelectedServices] = useState<
		PricingEstimationService[]
	>(() => getInitialServices(serviceBreakdown));
	const [guestCount, setGuestCount] = useState<string>(() =>
		String(persistedEstimate?.guestCount ?? ''),
	);

	useEffect(() => {
		if (!isModalOpen) return;
		setSelectedServices(getInitialServices(serviceBreakdown));
		setGuestCount(String(persistedEstimate?.guestCount ?? ''));
	}, [isModalOpen, serviceBreakdown, persistedEstimate?.guestCount]);

	const [weddingDate, setWeddingDate] = useState<string>(() =>
		toDateString(persistedEstimate?.weddingDate),
	);

	useEffect(() => {
		if (!isModalOpen) return;
		setWeddingDate(toDateString(persistedEstimate?.weddingDate));
	}, [isModalOpen, persistedEstimate?.weddingDate]);

	const isFormDirty = useMemo(() => {
		const initial = new Set(getInitialServices(serviceBreakdown));
		const current = new Set(selectedServices);
		if (initial.size !== current.size) return true;
		if (selectedServices.some((s) => !initial.has(s))) return true;
		const initialCount = persistedEstimate?.guestCount ?? null;
		const currentCount = guestCount ? Number(guestCount) : null;
		if (currentCount !== initialCount) return true;
		return weddingDate !== toDateString(persistedEstimate?.weddingDate);
	}, [
		selectedServices,
		serviceBreakdown,
		guestCount,
		persistedEstimate?.guestCount,
		weddingDate,
		persistedEstimate?.weddingDate,
	]);

	const toggleService = (key: string) => {
		if (!isService(key)) return;
		setSelectedServices((prev) =>
			prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
		);
	};

	return {
		selectedServices,
		weddingDate,
		setWeddingDate,
		isFormDirty,
		toggleService,
		guestCount,
		setGuestCount,
	};
};
